/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  Platform,
  RefreshControl,
  Share,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { supabase } from '../../shared/supabase/supabaseClient';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { NumericTextInput } from '../../shared/ui/NumericTextInput'
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad'
import { showError, showSuccess } from '../../shared/ui/appToast';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { tabBarListContentStyle } from '../../shared/layout/tabBarListContentStyle'
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import type { ResourceTimeOffCreateInput } from '../components/ResourceTimeOffEditor';
import { CreateAgendaModal } from '../components/CreateAgendaModal';
import { AgendaAdminModal, type TabKey as AgendaAdminTabKey } from '../components/AgendaAdminModal';
import { AgendaKpiStrip } from '../components/AgendaKpiStrip';
import { AgendaListActions } from '../components/AgendaListActions';
import { AgendaSummaryCard } from '../components/AgendaSummaryCard';
import { agenda, agendaFonts } from '../domain/agendaTheme';
import { ticketColors } from '../../shared/theme/ticketColors';
import { isValidHm } from '../domain/dayBoardLayout';
import type {
  AppointmentResource,
  AppointmentResourceKind,
  BookingDepositPolicy,
  BookingPaymentMode,
  BookingSettings,
  BookingSlotIntervalMinutes,
  ResourceHourDay,
  ResourceHourShift,
  ResourceTimeOff,
  ServiceProductOption,
} from '../domain/types';
import {
  applyWeekdayTemplate,
  hoursToWeekDraft,
  RESOURCE_DAY_LABELS,
  summarizeResourceHours,
  weekDraftToHours,
} from '../domain/types';
import {
  activateResource,
  createResource,
  createResourceTimeOff,
  deactivateResource,
  deleteResourceTimeOff,
  fetchStoreResourceHours,
  getBookingSettings,
  getBranchBookingUrl,
  listResourceServiceIds,
  listResourceServiceCounts,
  listResourceTimeOff,
  listResources,
  listAgendaEmployees,
  linkResourceToEmployee,
  listServiceProducts,
  setResourceHours,
  setResourceServices,
  updateBookingSettings,
  updateResource,
} from '../infra/appointmentsRepo';
import { resolveSessionEmployee } from '../../auth/application/sessionEmployeeResolver';

const PAYMENT_MODES: { key: BookingPaymentMode; label: string }[] = [
  { key: 'none', label: 'Sin cobro' },
  { key: 'deposit', label: 'Seña' },
  { key: 'full', label: 'Pago total' },
];

const DEPOSIT_POLICIES: { key: BookingDepositPolicy; label: string; helper: string }[] = [
  { key: 'keep', label: 'Conservar', helper: 'La seña queda para el local.' },
  { key: 'refund', label: 'Devolver', helper: 'Se inicia la devolución a Mercado Pago.' },
];

export function AppointmentResourcesScreen() {
  const insets = useSafeAreaInsets();
  const { layoutWide: isWide, contentMaxWidth } = useContentLayout('settingsWide');
  const { entitlements } = usePlanEntitlements();
  const orgId = entitlements?.organization_id;
  const branchId = useSessionStore((s) => s.currentBranchId);
  const { canView, canEdit, canManageAgenda, canViewOthers } = useBranchPermission('Agenda');
  const hasAppointments = useHasBusinessCapability('appointments');
  const isOwner = entitlements?.is_owner === true;
  const canSeeAllResources = canViewOthers || isOwner;

  const [resources, setResources] = useState<AppointmentResource[]>([]);
  /** Servicios explícitos por agenda; sin entrada = "Todos los servicios". */
  const [serviceCounts, setServiceCounts] = useState<Map<string, number>>(new Map());
  const loadSequence = useRef(0);
  const [noLinkedResource, setNoLinkedResource] = useState(false);
  const [settings, setSettings] = useState<BookingSettings | null>(null);
  const [bookingUrl, setBookingUrl] = useState<string | null | undefined>(undefined);
  /** Start true so first paint shows booking chrome (not the short "sin perfil" flash). */
  const [loading, setLoading] = useState(true);
  /** Mobile: booking panel starts collapsed so agendas lead. Wide always shows the rail body. */
  const [bookingExpanded, setBookingExpanded] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmployeeId, setNewEmployeeId] = useState<string | null>(null);
  const [newResourceKind, setNewResourceKind] = useState<AppointmentResourceKind>('person');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [agendaEmployees, setAgendaEmployees] = useState<Array<{ id: string; name: string; role: string; isOwner?: boolean }>>([]);
  const employeeNameById = useMemo(
    () => new Map(agendaEmployees.map((e) => [e.id, e.name])),
    [agendaEmployees],
  );
  const [creating, setCreating] = useState(false);
  const [bookingEnabledDraft, setBookingEnabledDraft] = useState(false);
  const [paymentModeDraft, setPaymentModeDraft] = useState<BookingPaymentMode>('none');
  /** Seña: exactly one of amount | percent (XOR). */
  const [depositKindDraft, setDepositKindDraft] = useState<'amount' | 'percent'>('amount');
  const [depositAmountDraft, setDepositAmountDraft] = useState('');
  const [depositPercentDraft, setDepositPercentDraft] = useState('');
  const [cancelHoursDraft, setCancelHoursDraft] = useState('2');
  const [leadHoursDraft, setLeadHoursDraft] = useState('0');
  const [horizonDaysDraft, setHorizonDaysDraft] = useState('7');
  const [expireMinutesDraft, setExpireMinutesDraft] = useState('30');
  const [slotIntervalDraft, setSlotIntervalDraft] = useState<BookingSlotIntervalMinutes>(15);
  const [waitlistEnabledDraft, setWaitlistEnabledDraft] = useState(false);
  const [waitlistOfferMinutesDraft, setWaitlistOfferMinutesDraft] = useState('15');
  const [lateCancelPolicyDraft, setLateCancelPolicyDraft] = useState<BookingDepositPolicy>('keep');
  const [noShowPolicyDraft, setNoShowPolicyDraft] = useState<BookingDepositPolicy>('keep');
  const [savingBooking, setSavingBooking] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  const [hoursDraft, setHoursDraft] = useState<ResourceHourDay[]>([]);
  const [savingHours, setSavingHours] = useState(false);

  const [adminResourceId, setAdminResourceId] = useState<string | null>(null);
  const [adminActiveTab, setAdminActiveTab] = useState<AgendaAdminTabKey>('horarios');
  const [adminInitialHours, setAdminInitialHours] = useState<ResourceHourDay[]>([]);
  const [adminInitialServiceIds, setAdminInitialServiceIds] = useState<string[]>([]);
  const [adminInitialEmployeeId, setAdminInitialEmployeeId] = useState<string | null>(null);

  const [timeOffList, setTimeOffList] = useState<ResourceTimeOff[]>([]);
  const [timeOffLoading, setTimeOffLoading] = useState(false);
  const [creatingTimeOff, setCreatingTimeOff] = useState(false);
  const [deletingTimeOff, setDeletingTimeOff] = useState(false);

  const [allServices, setAllServices] = useState<ServiceProductOption[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [savingServices, setSavingServices] = useState(false);
  const [statusTarget, setStatusTarget] = useState<{
    id: string;
    name: string;
    isActive: boolean;
  } | null>(null);
  const [savingRename, setSavingRename] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const lastLoadStartedAt = useRef(0);
  const load = useCallback(async () => {
    if (!branchId || !hasAppointments || !canView) return;
    const sequence = ++loadSequence.current;
    lastLoadStartedAt.current = Date.now();
    setLoading(true);
    try {
      const [res, booking, employees, sessionEmp, authResult, bookingLink] = await Promise.all([
        listResources(branchId),
        getBookingSettings(branchId).catch(() => null),
        listAgendaEmployees(branchId).catch(() => []),
        canManageAgenda || canSeeAllResources
          ? Promise.resolve({ employeeId: null, isEmployee: false, isActive: true })
          : resolveSessionEmployee(),
        canManageAgenda || canSeeAllResources
          ? Promise.resolve({ data: { user: null } })
          : supabase.auth.getUser(),
        getBranchBookingUrl(branchId).catch(() => null),
      ]);
      const empId = sessionEmp?.employeeId ?? null;
      const authUserId = authResult.data.user?.id ?? null;
      const ownerScope = canSeeAllResources || canManageAgenda || !sessionEmp?.isEmployee || res.some((r) => r.userId === authUserId);
      const counts = await listResourceServiceCounts(res.map((r) => r.id)).catch(() => new Map<string, number>());
      if (sequence !== loadSequence.current) return;
      const scoped = canManageAgenda
        ? res
        : ownerScope
          ? res
          : empId
            ? res.filter((r) => r.employeeId === empId)
            : [];
      setNoLinkedResource(!canManageAgenda && !ownerScope && scoped.length === 0);
      setResources(scoped);
      setServiceCounts(counts);
      setAgendaEmployees(employees);
      setSettings(booking);
      setBookingUrl(bookingLink);
      if (booking) {
        setBookingEnabledDraft(booking.bookingEnabled);
        setPaymentModeDraft(booking.bookingPaymentMode);
        const hasAmount = booking.bookingDepositAmount != null && booking.bookingDepositAmount > 0;
        const hasPercent = booking.bookingDepositPercent != null && booking.bookingDepositPercent > 0;
        // Amount wins when both set (legacy); XOR UI keeps only one.
        if (hasAmount) {
          setDepositKindDraft('amount');
          setDepositAmountDraft(String(booking.bookingDepositAmount));
          setDepositPercentDraft('');
        } else if (hasPercent) {
          setDepositKindDraft('percent');
          setDepositAmountDraft('');
          setDepositPercentDraft(String(booking.bookingDepositPercent));
        } else {
          setDepositKindDraft('amount');
          setDepositAmountDraft('');
          setDepositPercentDraft('');
        }
        setCancelHoursDraft(String(booking.bookingCancelHours));
        setLeadHoursDraft(String(booking.bookingMinLeadHours));
        setHorizonDaysDraft(String(booking.bookingHorizonDays));
        setExpireMinutesDraft(String(booking.bookingUnpaidExpireMinutes));
        setSlotIntervalDraft(booking.bookingSlotIntervalMinutes);
        setWaitlistEnabledDraft(booking.bookingWaitlistEnabled);
        setWaitlistOfferMinutesDraft(String(booking.bookingWaitlistOfferMinutes));
        setLateCancelPolicyDraft(booking.bookingLateCancelPolicy);
        setNoShowPolicyDraft(booking.bookingNoShowPolicy);
      }
    } catch (e) {
      showError('Agendas', e instanceof Error ? e.message : 'No se pudo cargar.');
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [branchId, canManageAgenda, canSeeAllResources, canView, hasAppointments]);

  useScreenLoad('AppointmentResources', load, { refreshOnFocus: false });

  useEffect(() => {
    void load();
  }, [load]);

  // Recarga silenciosa al volver: la lista de agendas y el picker «Persona
  // asignada» consumen datos que se mutan desde Equipo y otras superficies —
  // sin esto quedaban stale hasta F5. Solo refresca el roster: un load completo
  // pisaría los drafts de «Reglas de reserva» si el usuario vuelve a mitad de
  // edición. El dedupe por timestamp evita el doble fetch del mount inicial.
  useFocusEffect(
    useCallback(() => {
      if (!branchId || !hasAppointments || !canView) return;
      if (Date.now() - lastLoadStartedAt.current < 1500) return;
      void (async () => {
        const sequence = loadSequence.current;
        try {
          const [res, employees] = await Promise.all([
            listResources(branchId),
            listAgendaEmployees(branchId).catch(() => []),
          ]);
          const counts = await listResourceServiceCounts(res.map((r) => r.id)).catch(
            () => new Map<string, number>(),
          );
          if (sequence !== loadSequence.current) return;
          // El scoping por empleado de sesión vive en load(); acá solo se
          // actualiza la lista cuando el viewer la ve completa.
          if (canManageAgenda || canSeeAllResources) {
            setResources((current) => (res.length > 0 ? res : current.length > 0 ? current : res));
          }
          setServiceCounts(counts);
          setAgendaEmployees(employees);
        } catch {
          // Silencioso: el load completo con error UX corre por useScreenLoad.
        }
      })();
    }, [branchId, canManageAgenda, canSeeAllResources, canView, hasAppointments])
  );

  const openAgendaAdmin = (item: AppointmentResource) => {
    setAdminResourceId(item.id);
    setAdminActiveTab('horarios');
    const initialHours = hoursToWeekDraft(item.hours);
    setHoursDraft(initialHours);
    setAdminInitialHours(initialHours);
    setAdminInitialEmployeeId(item.employeeId);
    setAdminInitialServiceIds([]);
    setSelectedServiceIds([]);
    setAllServices([]);
    setTimeOffList([]);
  };

  const closeAgendaAdmin = () => {
    setAdminResourceId(null);
    setHoursDraft([]);
    setSelectedServiceIds([]);
    setAllServices([]);
    setTimeOffList([]);
  };

  const loadAdminServices = useCallback(async () => {
    if (!adminResourceId || !orgId) return;
    try {
      const [services, assigned] = await Promise.all([
        listServiceProducts(orgId),
        listResourceServiceIds(adminResourceId),
      ]);
      setAllServices(services);
      setSelectedServiceIds(assigned);
      setAdminInitialServiceIds(assigned);
    } catch (e) {
      showError('Servicios', e instanceof Error ? e.message : 'No se pudo cargar.');
    }
  }, [adminResourceId, orgId]);

  const loadAdminTimeOff = useCallback(async () => {
    if (!adminResourceId) return;
    setTimeOffLoading(true);
    try {
      setTimeOffList(await listResourceTimeOff(adminResourceId));
    } catch (e) {
      showError('Ausencias', e instanceof Error ? e.message : 'No se pudo cargar.');
    } finally {
      setTimeOffLoading(false);
    }
  }, [adminResourceId]);

  const handleAdminTabChange = (tab: AgendaAdminTabKey) => {
    setAdminActiveTab(tab);
    if (tab === 'servicios' && allServices.length === 0) {
      void loadAdminServices();
    } else if (tab === 'ausencias' && timeOffList.length === 0) {
      void loadAdminTimeOff();
    }
  };

  const onSaveAgendaHours = async () => {
    if (!adminResourceId) return;
    const validationError = validateHoursDraft();
    if (validationError) {
      showError('Horarios', validationError);
      return;
    }
    setSavingHours(true);
    try {
      await setResourceHours(adminResourceId, weekDraftToHours(hoursDraft));
      setAdminInitialHours(hoursDraft);
      await load();
      // No cerrar el modal: servicios u otros tabs pueden tener drafts propios.
      showSuccess('Horarios', 'Horarios guardados.');
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'horarios', error: e, title: 'Horarios', userMessage: 'No se pudo guardar.' });
    } finally {
      setSavingHours(false);
    }
  };

  const onSaveAgendaServices = async () => {
    if (!adminResourceId) return;
    setSavingServices(true);
    try {
      await setResourceServices(adminResourceId, selectedServiceIds);
      setAdminInitialServiceIds(selectedServiceIds);
      setServiceCounts((prev) => {
        const next = new Map(prev);
        if (selectedServiceIds.length === 0) next.delete(adminResourceId);
        else next.set(adminResourceId, selectedServiceIds.length);
        return next;
      });
      showSuccess('Servicios', 'Servicios guardados.');
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'servicios', error: e, title: 'Servicios', userMessage: 'No se pudo guardar.' });
    } finally {
      setSavingServices(false);
    }
  };

  const onCreateAgendaTimeOff = async (input: ResourceTimeOffCreateInput) => {
    if (!adminResourceId) return;
    setCreatingTimeOff(true);
    try {
      await createResourceTimeOff({ resourceId: adminResourceId, ...input });
      await loadAdminTimeOff();
      showSuccess('Ausencia', 'Ausencia cargada.');
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'ausencia', error: e, title: 'Ausencia', userMessage: 'No se pudo guardar.' });
    } finally {
      setCreatingTimeOff(false);
    }
  };

  const onDeleteAgendaTimeOff = async (id: string) => {
    if (!adminResourceId) return;
    setDeletingTimeOff(true);
    try {
      await deleteResourceTimeOff(id);
      await loadAdminTimeOff();
      showSuccess('Ausencia', 'Ausencia eliminada.');
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'ausencia', error: e, title: 'Ausencia', userMessage: 'No se pudo eliminar.' });
    } finally {
      setDeletingTimeOff(false);
    }
  };

  const onChangeAgendaPersona = async (employeeId: string | null) => {
    if (!adminResourceId) return;
    try {
      await linkResourceToEmployee(adminResourceId, employeeId);
      setAdminInitialEmployeeId(employeeId);
      await load();
      showSuccess('Persona', employeeId ? 'Persona asignada.' : 'Persona quitada.');
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'persona', error: e, title: 'Persona', userMessage: 'No se pudo guardar.' });
    }
  };

  const onRenameAgenda = async (name: string) => {
    if (!adminResourceId || !canManageAgenda || savingRename) return;
    const resource = resources.find((r) => r.id === adminResourceId);
    if (!resource || name === resource.displayName) return;
    setSavingRename(true);
    try {
      await updateResource(resource.id, { displayName: name });
      await load();
      showSuccess('Agenda renombrada', name);
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'agendas', error: e, title: 'Agendas', userMessage: 'No se pudo cambiar el nombre.' });
      throw e;
    } finally {
      setSavingRename(false);
    }
  };

  const onUpdateAgendaColor = async (color: string) => {
    if (!adminResourceId || !canManageAgenda) return;
    const resource = resources.find((r) => r.id === adminResourceId);
    if (!resource || color === resource.color) return;
    try {
      await updateResource(resource.id, { color });
      await load();
      showSuccess('Color actualizado', resource.displayName);
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'agendas', error: e, title: 'Agendas', userMessage: 'No se pudo cambiar el color.' });
      throw e;
    }
  };

  const onDeactivateAgenda = () => {
    const resource = resources.find((r) => r.id === adminResourceId);
    if (resource) setStatusTarget({ id: resource.id, name: resource.displayName, isActive: true });
  };

  const onActivateAgenda = () => {
    const resource = resources.find((r) => r.id === adminResourceId);
    if (resource) setStatusTarget({ id: resource.id, name: resource.displayName, isActive: false });
  };

  const patchDay = (dayOfWeek: number, patch: Partial<ResourceHourDay>) => {
    setHoursDraft((prev) =>
      prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)),
    );
  };

  const addShift = (dayOfWeek: number) => {
    setHoursDraft((prev) =>
      prev.map((d) => {
        if (d.dayOfWeek !== dayOfWeek || d.shifts.length >= 3) return d;
        return { ...d, shifts: [...d.shifts, { startTime: '09:00', endTime: '13:00' }] };
      }),
    );
  };

  const removeShift = (dayOfWeek: number, index: number) => {
    setHoursDraft((prev) =>
      prev.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        const shifts = d.shifts.filter((_, i) => i !== index);
        return { ...d, shifts, isOpen: shifts.length > 0 && d.isOpen };
      }),
    );
  };

  const changeShift = (
    dayOfWeek: number,
    index: number,
    field: keyof ResourceHourShift,
    value: string,
  ) => {
    setHoursDraft((prev) =>
      prev.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        return {
          ...d,
          shifts: d.shifts.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
        };
      }),
    );
  };

  const onCopyFromStore = async () => {
    if (!branchId) return;
    try {
      const hours = await fetchStoreResourceHours(branchId);
      if (!hours) {
        showError(
          'Horarios',
          'La tienda no tiene horarios. Cargalos en Tienda online, o acá a mano.',
        );
        return;
      }
      setHoursDraft(hoursToWeekDraft(hours));
      showSuccess('Horarios copiados de la tienda');
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'horarios', error: e, title: 'Horarios', userMessage: 'No se pudo copiar.' });
    }
  };

  const validateHoursDraft = (): string | null => {
    for (const d of hoursDraft) {
      if (!d.isOpen) continue;
      const label = RESOURCE_DAY_LABELS[d.dayOfWeek];
      if (d.shifts.length === 0) {
        return `${label} está abierto sin horarios. Agregá al menos uno o cerralo.`;
      }
      for (const s of d.shifts) {
        if (!isValidHm(s.startTime) || !isValidHm(s.endTime)) {
          return `Hora inválida en ${label}. Usá formato HH:mm.`;
        }
        if (s.endTime <= s.startTime) {
          return `En ${label} el fin debe ser después del inicio.`;
        }
      }
    }
    return null;
  };

  const onCreate = async () => {
    if (!orgId || !branchId) return;
    const name = newName.trim();
    if (!name) {
      showError('Agendas', 'Ingresá un nombre para la agenda.');
      return;
    }
    setCreating(true);
    try {
      const created = await createResource({
        organizationId: orgId,
        branchId,
        displayName: name,
        employeeId: newEmployeeId,
        resourceKind: newResourceKind,
      });
      setNewName('');
      setNewEmployeeId(null);
      setNewResourceKind('person');
      setCreateModalVisible(false);
      await load();
      showSuccess('Agenda creada', name);
      openAgendaAdmin(created);
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'agendas', error: e, title: 'Agendas', userMessage: 'No se pudo crear.' });
    } finally {
      setCreating(false);
    }
  };

  const confirmStatusChange = async () => {
    if (!statusTarget || deactivating) return;
    const { id, name, isActive } = statusTarget;
    setDeactivating(true);
    try {
      if (isActive) await deactivateResource(id);
      else await activateResource(id);
      setStatusTarget(null);
      setAdminResourceId(null);
      await load();
      showSuccess(isActive ? 'Agenda desactivada' : 'Agenda reactivada', name);
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'toggle-active', error: e, title: 'Agendas', userMessage: `No se pudo ${isActive ? 'desactivar' : 'reactivar'}.` });
    } finally {
      setDeactivating(false);
    }
  };

  const bookingDirtyCount = useMemo(() => {
    if (!settings) return 0;
    const amountSaved =
      settings.bookingDepositAmount != null && settings.bookingDepositAmount > 0
        ? String(settings.bookingDepositAmount)
        : '';
    const percentSaved =
      settings.bookingDepositPercent != null && settings.bookingDepositPercent > 0
        ? String(settings.bookingDepositPercent)
        : '';
    const amountDraft = depositKindDraft === 'amount' ? depositAmountDraft.trim() : '';
    const percentDraft = depositKindDraft === 'percent' ? depositPercentDraft.trim() : '';
    const changed = [
      bookingEnabledDraft !== settings.bookingEnabled,
      paymentModeDraft !== settings.bookingPaymentMode,
      amountDraft !== amountSaved || percentDraft !== percentSaved,
      cancelHoursDraft.trim() !== String(settings.bookingCancelHours),
      leadHoursDraft.trim() !== String(settings.bookingMinLeadHours),
      horizonDaysDraft.trim() !== String(settings.bookingHorizonDays),
      expireMinutesDraft.trim() !== String(settings.bookingUnpaidExpireMinutes),
      slotIntervalDraft !== settings.bookingSlotIntervalMinutes,
      waitlistEnabledDraft !== settings.bookingWaitlistEnabled,
      waitlistOfferMinutesDraft.trim() !== String(settings.bookingWaitlistOfferMinutes),
      lateCancelPolicyDraft !== settings.bookingLateCancelPolicy,
      noShowPolicyDraft !== settings.bookingNoShowPolicy,
    ];
    return changed.filter(Boolean).length;
  }, [
    settings,
    bookingEnabledDraft,
    paymentModeDraft,
    depositKindDraft,
    depositAmountDraft,
    depositPercentDraft,
    cancelHoursDraft,
    leadHoursDraft,
    horizonDaysDraft,
    expireMinutesDraft,
    slotIntervalDraft,
    waitlistEnabledDraft,
    waitlistOfferMinutesDraft,
    lateCancelPolicyDraft,
    noShowPolicyDraft,
  ]);

  const bookingDirty = bookingDirtyCount > 0;

  const resetBookingDrafts = useCallback(() => {
    if (!settings) return;
    setBookingEnabledDraft(settings.bookingEnabled);
    setPaymentModeDraft(settings.bookingPaymentMode);
    const hasAmount = settings.bookingDepositAmount != null && settings.bookingDepositAmount > 0;
    const hasPercent = settings.bookingDepositPercent != null && settings.bookingDepositPercent > 0;
    if (hasAmount) {
      setDepositKindDraft('amount');
      setDepositAmountDraft(String(settings.bookingDepositAmount));
      setDepositPercentDraft('');
    } else if (hasPercent) {
      setDepositKindDraft('percent');
      setDepositAmountDraft('');
      setDepositPercentDraft(String(settings.bookingDepositPercent));
    } else {
      setDepositKindDraft('amount');
      setDepositAmountDraft('');
      setDepositPercentDraft('');
    }
    setCancelHoursDraft(String(settings.bookingCancelHours));
    setLeadHoursDraft(String(settings.bookingMinLeadHours));
    setHorizonDaysDraft(String(settings.bookingHorizonDays));
    setExpireMinutesDraft(String(settings.bookingUnpaidExpireMinutes));
    setSlotIntervalDraft(settings.bookingSlotIntervalMinutes);
    setWaitlistEnabledDraft(settings.bookingWaitlistEnabled);
    setWaitlistOfferMinutesDraft(String(settings.bookingWaitlistOfferMinutes));
    setLateCancelPolicyDraft(settings.bookingLateCancelPolicy);
    setNoShowPolicyDraft(settings.bookingNoShowPolicy);
  }, [settings]);

  const copyBookingLink = async () => {
    const url = bookingUrl;
    if (!url) return;
    try {
      await Clipboard.setStringAsync(url);
      setCopiedLink(true);
      showSuccess('Link copiado', 'Pegalo en WhatsApp, Instagram o donde quieras.');
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopiedLink(false), 2500);
    } catch {
      showError('No se pudo copiar', 'Seleccioná el link y copialo a mano.');
    }
  };

  const shareBookingLink = async () => {
    const url = bookingUrl;
    if (!url) return;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && !navigator.share) {
        await copyBookingLink();
        return;
      }
      await Share.share({ message: url });
    } catch {
      return;
    }
  };

  const saveBookingSettings = async () => {
    if (!branchId || !settings || savingBooking) return;

    const parseIntInRange = (draft: string, label: string, min: number, max: number) => {
      const n = Number(draft.trim());
      if (!Number.isFinite(n) || n < min || n > max) {
        showError('Reglas de reserva', `${label}: valor entre ${min} y ${max}.`);
        return null;
      }
      return Math.round(n);
    };

    const cancelHours = parseIntInRange(cancelHoursDraft, 'Cancelación online', 0, 168);
    if (cancelHours == null) return;
    const leadHours = parseIntInRange(leadHoursDraft, 'Anticipación mínima', 0, 168);
    if (leadHours == null) return;
    const horizonDays = parseIntInRange(horizonDaysDraft, 'Días hacia adelante', 1, 90);
    if (horizonDays == null) return;
    const expireMinutes = parseIntInRange(expireMinutesDraft, 'Seña sin pagar', 0, 10080);
    if (expireMinutes == null) return;
    const offerMinutes = parseIntInRange(waitlistOfferMinutesDraft, 'Minutos de oferta', 5, 1440);
    if (offerMinutes == null) return;

    let depositAmount: number | null = null;
    let depositPercent: number | null = null;
    if (paymentModeDraft === 'deposit') {
      if (depositKindDraft === 'amount') {
        const raw = depositAmountDraft.trim();
        if (!raw) {
          showError('Seña', 'Ingresá el monto fijo de la seña.');
          return;
        }
        const n = Number(raw.replace(',', '.'));
        if (!Number.isFinite(n) || n <= 0) {
          showError('Seña', 'Monto inválido (debe ser mayor a 0).');
          return;
        }
        depositAmount = n;
      } else {
        const raw = depositPercentDraft.trim();
        if (!raw) {
          showError('Seña', 'Ingresá el porcentaje de la seña.');
          return;
        }
        const n = Number(raw.replace(',', '.'));
        if (!Number.isFinite(n) || n <= 0 || n > 100) {
          showError('Seña', 'Porcentaje entre 1 y 100.');
          return;
        }
        depositPercent = n;
      }
    }

    const patch: BookingSettings = {
      bookingEnabled: bookingEnabledDraft,
      bookingPaymentMode: paymentModeDraft,
      bookingDepositAmount: depositAmount,
      bookingDepositPercent: depositPercent,
      bookingCancelHours: cancelHours,
      bookingMinLeadHours: leadHours,
      bookingHorizonDays: horizonDays,
      bookingUnpaidExpireMinutes: expireMinutes,
      bookingSlotIntervalMinutes: slotIntervalDraft,
      bookingWaitlistEnabled: waitlistEnabledDraft,
      bookingWaitlistOfferMinutes: offerMinutes,
      bookingLateCancelPolicy: lateCancelPolicyDraft,
      bookingNoShowPolicy: noShowPolicyDraft,
    };

    setSavingBooking(true);
    try {
      await updateBookingSettings(branchId, patch);
      setSettings(patch);
      setDepositAmountDraft(depositAmount != null ? String(depositAmount) : '');
      setDepositPercentDraft(depositPercent != null ? String(depositPercent) : '');
      setCancelHoursDraft(String(cancelHours));
      setLeadHoursDraft(String(leadHours));
      setHorizonDaysDraft(String(horizonDays));
      setExpireMinutesDraft(String(expireMinutes));
      setSlotIntervalDraft(slotIntervalDraft);
      setWaitlistOfferMinutesDraft(String(offerMinutes));
      showSuccess('Guardado', 'Reglas de reserva actualizadas.');
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'reglas-de-reserva', error: e, title: 'Reglas de reserva', userMessage: 'No se pudo guardar.' });
    } finally {
      setSavingBooking(false);
    }
  };

  const listContentStyle = useMemo(
    () =>
      tabBarListContentStyle({
        bottomInset: insets.bottom,
        // Do not center empty content: ListHeader holds the page chrome; centering
        // would park that chrome mid-screen until resources load (~1s jump).
        baseContentStyle: styles.listContent,
      }),
    [insets.bottom],
  );

  const bookingStatusLine = useMemo(() => {
    if (settings == null && !loading) return 'Sin perfil público';
    if (!settings) return 'Cargando…';
    const payment =
      PAYMENT_MODES.find((m) => m.key === paymentModeDraft)?.label ?? 'Sin cobro';
    const interval = `cada ${slotIntervalDraft} min`;
    const base = bookingEnabledDraft
      ? `Activas · ${interval} · ${payment}`
      : `Pausadas · ${interval} · ${payment}`;
    return base;
  }, [settings, loading, bookingEnabledDraft, paymentModeDraft, slotIntervalDraft]);

  useEffect(() => {
    if (bookingDirty && !isWide) setBookingExpanded(true);
  }, [bookingDirty, isWide]);

  const bookingDirtySuffix = bookingDirty ? (
    <Text style={styles.bookingStatusDirty}>
      {` · ${bookingDirtyCount} ${bookingDirtyCount === 1 ? 'cambio' : 'cambios'} sin guardar`}
    </Text>
  ) : null;

  const showBookingBody = isWide || bookingExpanded;
  const adminResource = resources.find((r) => r.id === adminResourceId) ?? null;
  const adminHoursDirty = JSON.stringify(hoursDraft) !== JSON.stringify(adminInitialHours);
  const adminServicesDirty =
    JSON.stringify([...selectedServiceIds].sort()) !== JSON.stringify([...adminInitialServiceIds].sort());
  const adminEmployeeDirty = (adminResource?.employeeId ?? null) !== adminInitialEmployeeId;
  const adminHasUnsavedChanges = adminHoursDirty || adminServicesDirty || adminEmployeeDirty;

  const kpiBlock = (
    <AgendaKpiStrip
      total={resources.length}
      active={resources.filter((r) => r.isActive).length}
      inactive={resources.filter((r) => !r.isActive).length}
    />
  );

  const openCreateModal = () => {
    // Solo-dueño (sin empleados invitados): preseleccionar su membresía para
    // que "Nueva agenda → Crear" ya quede asignada a sí mismo (Booksy-style).
    // Si hay empleados, la persona se elige a mano (Agenda libre por defecto).
    const ownerOption = agendaEmployees.find((e) => e.isOwner);
    const soloOwner = ownerOption != null && agendaEmployees.every((e) => e.isOwner);
    setNewName(soloOwner ? ownerOption.name : '');
    setNewEmployeeId(soloOwner ? ownerOption.id : null);
    setNewResourceKind('person');
    setCreateModalVisible(true);
  };

  const actionsBlock = (
    <AgendaListActions onCreate={openCreateModal} canManage={canManageAgenda} />
  );

  const servicesLabelFor = (id: string): string => {
    const count = serviceCounts.get(id);
    if (count == null) return 'Todos los servicios';
    return count === 1 ? '1 servicio' : `${count} servicios`;
  };

  const agendasList = (
    <>
      {resources.length > 0 ? (
        <View style={styles.listCard}>
          <View style={styles.listCardHead}>
            <Text style={styles.secTitleSm}>Agendas</Text>
          </View>
          {resources.map((item, index) => (
            <AgendaSummaryCard
              key={item.id}
              displayName={item.displayName}
              color={item.color}
              employeeName={item.employeeId ? employeeNameById.get(item.employeeId) ?? null : null}
              resourceKind={item.resourceKind}
              hoursSummary={summarizeResourceHours(item.hours)}
              servicesText={servicesLabelFor(item.id)}
              index={index}
              isActive={item.isActive}
              isLast={index === resources.length - 1}
              onPress={() => openAgendaAdmin(item)}
            />
          ))}
        </View>
      ) : null}

      {!loading && resources.length === 0 ? (
        <ModuleEmptyState
          testID={noLinkedResource ? 'appointments.resources.no-linked' : 'appointments.resources.empty'}
          title={noLinkedResource ? 'Sin agenda vinculada' : 'Sin agendas todavía'}
          subtitle={
            noLinkedResource
              ? 'Tu usuario no tiene agenda en esta sucursal. Pedile al dueño que te asocie.'
              : 'Creá la primera para tomar turnos.'
          }
          primaryAction={
            !noLinkedResource && canManageAgenda
              ? { label: 'Crear agenda', onPress: openCreateModal }
              : undefined
          }
        />
      ) : null}
    </>
  );

  const bookingBody =
    settings == null && !loading ? (
      <Text style={styles.hint}>Esta sucursal no tiene perfil público.</Text>
    ) : (
      <>
        <View style={styles.rulesGroup}>
          <Text style={styles.rulesGroupTitle}>Reserva online</Text>
          <View style={styles.row}>
            <View style={styles.rowLabelWrap}>
              <Text style={styles.rowLabel}>Recibir reservas online</Text>
              <Text style={styles.hint}>Apagado = el portal deja de mostrar turnos.</Text>
            </View>
            <Switch
              value={bookingEnabledDraft}
              onValueChange={setBookingEnabledDraft}
              trackColor={{ false: agenda.border, true: agenda.brand }}
              thumbColor={agenda.surface}
            />
          </View>
          {bookingUrl === undefined ? (
            <Text style={styles.hint}>Cargando link…</Text>
          ) : bookingUrl === null ? (
            <Text style={styles.hint}>
              Tu tienda todavía no tiene link público. Activala desde Configuración → Tienda online.
            </Text>
          ) : (
            <>
              <View style={styles.linkBox}>
                <Text style={styles.linkUrl} numberOfLines={1} selectable>
                  {bookingUrl.replace(/^https?:\/\//, '')}
                </Text>
                <TouchableOpacity
                  onPress={() => void copyBookingLink()}
                  style={styles.linkActionBtn}
                  accessibilityRole="button"
                  accessibilityLabel="Copiar link de reserva"
                >
                  <Text style={styles.linkAction}>{copiedLink ? 'Copiado' : 'Copiar'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => void shareBookingLink()}
                  style={styles.linkActionBtn}
                  accessibilityRole="button"
                  accessibilityLabel="Compartir link de reserva"
                >
                  <Text style={styles.linkAction}>Compartir</Text>
                </TouchableOpacity>
              </View>
              <Text style={styles.hint}>
                Pegalo en Instagram o WhatsApp — tus clientes reservan solos.
              </Text>
            </>
          )}
          {bookingEnabledDraft ? (
            <>
              <View style={styles.row}>
                <View style={styles.rowLabelWrap}>
                  <Text style={styles.rowLabel}>Lista de espera online</Text>
                  <Text style={styles.hint}>
                    Si no hay hueco, el cliente se anota y le avisamos cuando se libera.
                  </Text>
                </View>
                <Switch
                  value={waitlistEnabledDraft}
                  onValueChange={setWaitlistEnabledDraft}
                  trackColor={{ false: agenda.border, true: agenda.brand }}
                  thumbColor={agenda.surface}
                />
              </View>
              {waitlistEnabledDraft ? (
                <View style={styles.fieldNarrow}>
                  <Text style={styles.fieldLabel}>Minutos para aceptar la oferta</Text>
                  <NumericTextInput
                    kind="integer"
                    style={styles.input}
                    value={waitlistOfferMinutesDraft}
                    onChangeText={setWaitlistOfferMinutesDraft}
                    placeholder="15"
                    placeholderTextColor={agenda.ink2}
                  />
                  <Text style={styles.fieldHelper}>
                    Si no acepta a tiempo, la oferta pasa al siguiente de la lista.
                  </Text>
                </View>
              ) : null}
            </>
          ) : null}
        </View>
        <View style={styles.rulesGroup}>
          <Text style={styles.rulesGroupTitle}>Grilla y ventanas</Text>
          <Text style={styles.fieldLabel}>Mostrar turnos cada</Text>
          <View style={styles.modeRow}>
            {([15, 30] as const).map((mins) => {
              const active = slotIntervalDraft === mins;
              return (
                <TouchableOpacity
                  key={mins}
                  style={[styles.chip, active && styles.chipActive]}
                  onPress={() => setSlotIntervalDraft(mins)}
                  accessibilityRole="button"
                  accessibilityLabel={`Mostrar turnos cada ${mins} minutos`}
                  accessibilityState={{ selected: active }}
                >
                  <Text style={[styles.chipText, active && styles.chipTextActive]}>
                    {mins} min
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <Text style={styles.hint}>Ej. cada 30 min: 10:00, 10:30, 11:00…</Text>
          <View style={styles.bookingGrid}>
            <View style={styles.bookingField}>
              <Text style={styles.fieldLabel}>Cancelación (horas)</Text>
              <NumericTextInput
                kind="integer"
                style={styles.input}
                value={cancelHoursDraft}
                onChangeText={setCancelHoursDraft}
                placeholder="2"
                placeholderTextColor={agenda.ink2}
              />
              <Text style={styles.fieldHelper}>Horas antes del turno</Text>
            </View>
            <View style={styles.bookingField}>
              <Text style={styles.fieldLabel}>Anticipación (horas)</Text>
              <NumericTextInput
                kind="integer"
                style={styles.input}
                value={leadHoursDraft}
                onChangeText={setLeadHoursDraft}
                placeholder="0"
                placeholderTextColor={agenda.ink2}
              />
              <Text style={styles.fieldHelper}>Mínimo para reservar</Text>
            </View>
            <View style={[styles.bookingField, styles.fieldNarrow]}>
              <Text style={styles.fieldLabel}>Días hacia adelante</Text>
              <NumericTextInput
                kind="integer"
                style={styles.input}
                value={horizonDaysDraft}
                onChangeText={setHorizonDaysDraft}
                placeholder="7"
                placeholderTextColor={agenda.ink2}
              />
              <Text style={styles.fieldHelper}>Días visibles</Text>
            </View>
          </View>
        </View>
        <View style={styles.rulesGroup}>
          <Text style={styles.rulesGroupTitle}>Cobro al reservar</Text>
          <View style={styles.modeRow}>
            {PAYMENT_MODES.map((m) => {
              const active = paymentModeDraft === m.key;
              return (
                <TouchableOpacity
                  key={m.key}
                  style={[styles.chip, active && styles.chipActive]}
                  onPress={() => setPaymentModeDraft(m.key)}
                  accessibilityRole="button"
                  accessibilityLabel={`Cobro al reservar: ${m.label}`}
                  accessibilityState={{ selected: active }}
                >
                  <Text style={[styles.chipText, active && styles.chipTextActive]}>{m.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
          {paymentModeDraft === 'deposit' ? (
            <View>
              <Text style={styles.fieldLabel}>Tipo de seña</Text>
              <View style={styles.modeRow}>
                {(
                  [
                    { key: 'amount' as const, label: 'Monto fijo' },
                    { key: 'percent' as const, label: 'Porcentaje' },
                  ] as const
                ).map((k) => {
                  const active = depositKindDraft === k.key;
                  return (
                    <TouchableOpacity
                      key={k.key}
                      style={[styles.chip, active && styles.chipActive]}
                      onPress={() => {
                        setDepositKindDraft(k.key);
                        if (k.key === 'amount') setDepositPercentDraft('');
                        else setDepositAmountDraft('');
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={`Tipo de seña: ${k.label}`}
                      accessibilityState={{ selected: active }}
                    >
                      <Text style={[styles.chipText, active && styles.chipTextActive]}>{k.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {depositKindDraft === 'amount' ? (
                <>
                  <Text style={styles.fieldLabel}>Seña (ARS)</Text>
                  <NumericTextInput
                    kind="decimalMoney"
                    style={styles.input}
                    value={depositAmountDraft}
                    onChangeText={setDepositAmountDraft}
                    placeholder="Ej. 5000"
                    placeholderTextColor={agenda.ink2}
                  />
                </>
              ) : (
                <>
                  <Text style={styles.fieldLabel}>Seña (%)</Text>
                  <NumericTextInput
                    kind="integer"
                    style={styles.input}
                    value={depositPercentDraft}
                    onChangeText={setDepositPercentDraft}
                    placeholder="Ej. 30"
                    placeholderTextColor={agenda.ink2}
                  />
                </>
              )}
              <Text style={styles.hint}>Elegí monto fijo o porcentaje, no ambos.</Text>
            </View>
          ) : null}
          {paymentModeDraft === 'deposit' || paymentModeDraft === 'full' ? (
            <View style={styles.fieldNarrow}>
              <Text style={styles.fieldLabel}>
                {paymentModeDraft === 'deposit' ? 'Seña vence (minutos)' : 'Reserva vence (minutos)'}
              </Text>
              <NumericTextInput
                kind="integer"
                style={styles.input}
                value={expireMinutesDraft}
                onChangeText={setExpireMinutesDraft}
                placeholder="30"
                placeholderTextColor={agenda.ink2}
              />
              <Text style={styles.fieldHelper}>Sin pagar, la reserva se libera. 0 = no vence.</Text>
            </View>
          ) : null}
          {paymentModeDraft === 'deposit' || paymentModeDraft === 'full' ? (
            <Text style={styles.hint}>
              Para cobrar online necesitás Mercado Pago conectado en Ajustes.
            </Text>
          ) : null}
          {paymentModeDraft === 'deposit' || paymentModeDraft === 'full' ? (
            <View style={styles.policyBlock}>
              <Text style={styles.fieldLabel}>Si cancelan tarde</Text>
              <View style={styles.modeRow}>
                {DEPOSIT_POLICIES.map((policy) => {
                  const active = lateCancelPolicyDraft === policy.key;
                  return (
                    <TouchableOpacity
                      key={`late-${policy.key}`}
                      style={[styles.chip, active && styles.chipActive]}
                      onPress={() => setLateCancelPolicyDraft(policy.key)}
                      accessibilityRole="button"
                      accessibilityLabel={`Si cancelan tarde: ${policy.label}`}
                      accessibilityState={{ selected: active }}
                    >
                      <Text style={[styles.chipText, active && styles.chipTextActive]}>{policy.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <Text style={styles.hint}>
                {DEPOSIT_POLICIES.find((policy) => policy.key === lateCancelPolicyDraft)?.helper}
              </Text>
              <Text style={styles.fieldLabel}>Si no se presentan</Text>
              <View style={styles.modeRow}>
                {DEPOSIT_POLICIES.map((policy) => {
                  const active = noShowPolicyDraft === policy.key;
                  return (
                    <TouchableOpacity
                      key={`no-show-${policy.key}`}
                      style={[styles.chip, active && styles.chipActive]}
                      onPress={() => setNoShowPolicyDraft(policy.key)}
                      accessibilityRole="button"
                      accessibilityLabel={`Si no se presentan: ${policy.label}`}
                      accessibilityState={{ selected: active }}
                    >
                      <Text style={[styles.chipText, active && styles.chipTextActive]}>{policy.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <Text style={styles.hint}>
                {DEPOSIT_POLICIES.find((policy) => policy.key === noShowPolicyDraft)?.helper}
              </Text>
              <Text style={styles.hint}>Si cancelás desde el local, la seña se devuelve.</Text>
            </View>
          ) : null}
        </View>
        <View style={styles.bookingSaveRow}>
          {bookingDirty ? (
            <TouchableOpacity
              style={[styles.discardBtn, savingBooking && styles.btnDisabled]}
              onPress={() => setConfirmDiscard(true)}
              disabled={savingBooking || !canManageAgenda}
              accessibilityRole="button"
              accessibilityLabel="Descartar cambios de reglas de reserva"
            >
              <Text style={styles.discardBtnText}>Descartar</Text>
            </TouchableOpacity>
          ) : <View />}
          <TouchableOpacity
            style={[
              styles.primaryBtn,
              styles.bookingSaveBtn,
              (!bookingDirty || savingBooking) && styles.btnDisabled,
            ]}
            onPress={() => void saveBookingSettings()}
            disabled={!bookingDirty || savingBooking || !canManageAgenda}
            accessibilityRole="button"
            accessibilityLabel="Guardar reglas de reserva"
            accessibilityState={{ disabled: !bookingDirty || savingBooking || !canManageAgenda }}
          >
            <Text style={styles.primaryBtnText}>{savingBooking ? 'Guardando…' : 'Guardar'}</Text>
          </TouchableOpacity>
        </View>
      </>
    );

  const bookingBlock = (
    <View style={[styles.section, isWide && styles.bookingRail]}>
      {isWide ? (
        <>
          <Text style={styles.sectionTitle}>Reglas de reserva</Text>
          <Text style={[styles.bookingStatusLine, styles.bookingStatusRail]}>
            {bookingStatusLine}
            {bookingDirtySuffix}
          </Text>
        </>
      ) : (
        <TouchableOpacity
          style={styles.collapseHeader}
          onPress={() => setBookingExpanded((v) => !v)}
          accessibilityRole="button"
          accessibilityLabel={bookingExpanded ? 'Colapsar reglas de reserva' : 'Expandir reglas de reserva'}
          accessibilityState={{ expanded: bookingExpanded }}
        >
          <View style={styles.collapseHeaderMain}>
            <Text style={styles.sectionTitleInline}>Reglas de reserva</Text>
            <Text style={styles.bookingStatusLine} numberOfLines={1}>
              {bookingStatusLine}
              {bookingDirtySuffix}
            </Text>
          </View>
          <Ionicons
            name={bookingExpanded ? 'chevron-up' : 'chevron-down'}
            size={18}
            color={agenda.ink2}
          />
        </TouchableOpacity>
      )}
      {showBookingBody ? bookingBody : null}
    </View>
  );

  if (!hasAppointments) {
    return <ModuleEmptyState title="Agenda no disponible" subtitle="Este rubro no tiene Agenda." />;
  }
  if (!canView) {
    return <ModuleEmptyState title="Agenda no disponible" subtitle="Pedile al dueño que te habilite Agenda." />;
  }

  return (
    <ShellContentFrame style={styles.container} maxWidth={isWide ? contentMaxWidth : undefined}>
      <ModalAwareScrollView
        style={styles.scroll}
        contentContainerStyle={listContentStyle}
        keyboardShouldPersistTaps="handled"
        bottomOffset={24}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={() => void load()} />}
      >
        <View style={styles.pageIntro}>
          <Text style={styles.pageSubtitle}>
            {canManageAgenda
              ? 'Una agenda por profesional o cabina. Cada una con horario, servicios y ausencias.'
              : 'Tus agendas en esta sucursal.'}
          </Text>
          {actionsBlock}
        </View>

        {isWide ? (
          <View style={styles.workspaceRow}>
            <View style={[styles.mainCol, isWide && styles.mainColWide]}>
              {kpiBlock}
              {agendasList}
            </View>
            {canManageAgenda ? bookingBlock : null}
          </View>
        ) : (
          <>
            {canManageAgenda ? bookingBlock : null}
            {kpiBlock}
            {agendasList}
          </>
        )}
      </ModalAwareScrollView>
      <OperationalConfirmModal
        visible={confirmDiscard}
        title="Descartar cambios"
        message={`Se revierten ${bookingDirtyCount} ${bookingDirtyCount === 1 ? 'cambio' : 'cambios'} sin guardar en las reglas de reserva.`}
        confirmLabel="Descartar"
        destructive
        iconName="arrow-undo-outline"
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          resetBookingDrafts();
        }}
      />
      <OperationalConfirmModal
        visible={statusTarget != null}
        title={statusTarget?.isActive ? 'Desactivar agenda' : 'Reactivar agenda'}
        entityName={statusTarget?.name}
        message={
          statusTarget?.isActive
            ? 'No entra en turnos nuevos ni en reservas online. Los que ya están se quedan.'
            : 'Vuelve a entrar en turnos nuevos y en reservas online.'
        }
        confirmLabel={statusTarget?.isActive ? 'Desactivar' : 'Reactivar'}
        destructive={statusTarget?.isActive}
        iconName="calendar-outline"
        loading={deactivating}
        onClose={() => {
          if (!deactivating) setStatusTarget(null);
        }}
        onConfirm={() => void confirmStatusChange()}
      />
      <CreateAgendaModal
        visible={createModalVisible}
        creating={creating}
        canManage={canManageAgenda}
        name={newName}
        onNameChange={setNewName}
        employeeId={newEmployeeId}
        onEmployeeChange={setNewEmployeeId}
        resourceKind={newResourceKind}
        onResourceKindChange={(kind) => {
          setNewResourceKind(kind);
          // Lift/box y Equipo no llevan persona: si venía preseleccionado el dueño, limpiar.
          if (kind !== 'person' && newEmployeeId) {
            const ownerOption = agendaEmployees.find((e) => e.isOwner);
            setNewEmployeeId(null);
            if (ownerOption && newName === ownerOption.name) setNewName('');
          }
        }}
        employees={agendaEmployees}
        onCreate={() => void onCreate()}
        onClose={() => setCreateModalVisible(false)}
      />
      <AgendaAdminModal
        visible={adminResourceId !== null}
        resource={resources.find((r) => r.id === adminResourceId) ?? null}
        resourceIndex={adminResourceId ? resources.findIndex((r) => r.id === adminResourceId) : 0}
        isWide={isWide}
        canManage={canManageAgenda}
        agendaEmployees={agendaEmployees}
        allServices={allServices}
        selectedServiceIds={selectedServiceIds}
        hoursDraft={hoursDraft}
        timeOffList={timeOffList}
        timeOffLoading={timeOffLoading}
        savingHours={savingHours}
        savingServices={savingServices}
        creatingTimeOff={creatingTimeOff}
        deletingTimeOff={deletingTimeOff}
        activeTab={adminActiveTab}
        hasUnsavedChanges={adminHasUnsavedChanges}
        hoursDirty={adminHoursDirty}
        servicesDirty={adminServicesDirty}
        onClose={closeAgendaAdmin}
        onTabChange={handleAdminTabChange}
        onEmployeeChange={onChangeAgendaPersona}
        onSaveHours={onSaveAgendaHours}
        onSaveServices={onSaveAgendaServices}
        onSelectedServiceIdsChange={setSelectedServiceIds}
        onCreateTimeOff={onCreateAgendaTimeOff}
        onDeleteTimeOff={onDeleteAgendaTimeOff}
        onRename={onRenameAgenda}
        savingRename={savingRename}
        bookingUrl={bookingUrl}
        onUpdateColor={onUpdateAgendaColor}
        onPatchDay={patchDay}
        onAddShift={addShift}
        onRemoveShift={removeShift}
        onShiftChange={changeShift}
        onCopyFromStore={() => void onCopyFromStore()}
        onApplyWeekdays={() => setHoursDraft((d) => applyWeekdayTemplate(d))}
        onDeactivate={onDeactivateAgenda}
        onActivate={onActivateAgenda}
      />
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: agenda.bg },
  scroll: { flex: 1 },
  pageIntro: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pageSubtitle: {
    flex: 1,
    fontSize: 13,
    color: agenda.ink2,
    lineHeight: 18,
    fontFamily: agendaFonts.sans,
  },
  workspaceRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingHorizontal: 12,
  },
  mainCol: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  mainColWide: {
    flex: 1,
  },
  section: {
    marginHorizontal: 12,
    marginTop: 12,
    marginBottom: 8,
    padding: 14,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
  },
  listCard: {
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
    overflow: 'hidden',
  },
  listCardHead: {
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
  },
  secTitleSm: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: agenda.ink3,
    fontFamily: agendaFonts.sans,
  },
  rulesGroup: {
    borderTopWidth: 1,
    borderTopColor: agenda.border,
    paddingTop: 12,
    marginTop: 10,
  },
  rulesGroupTitle: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: agenda.ink3,
    marginBottom: 8,
    fontFamily: agendaFonts.sans,
  },
  linkBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: agenda.borderControl,
    borderRadius: 9,
    paddingHorizontal: 11,
    paddingVertical: 8,
    marginBottom: 6,
  },
  linkUrl: {
    flex: 1,
    fontSize: 12,
    color: agenda.ink2,
    fontFamily: agendaFonts.mono,
  },
  linkAction: {
    fontSize: 12,
    fontWeight: '800',
    color: agenda.brand,
    fontFamily: agendaFonts.sans,
  },
  linkActionBtn: {
    minHeight: 36,
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  fieldNarrow: {
    maxWidth: 170,
  },
  rowLabelWrap: {
    flex: 1,
    paddingRight: 12,
  },
  bookingStatusRail: {
    marginBottom: 10,
    marginTop: -6,
  },
  bookingRail: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 380,
    width: 380,
    maxWidth: 400,
    marginHorizontal: 0,
    marginTop: 0,
    alignSelf: 'flex-start',
  },
  collapseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minHeight: 44,
    marginBottom: 2,
  },
  collapseHeaderMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: agenda.ink,
    marginBottom: 10,
    fontFamily: agendaFonts.sans,
  },
  sectionTitleInline: {
    fontSize: 15,
    fontWeight: '700',
    color: agenda.ink,
    marginBottom: 0,
    fontFamily: agendaFonts.sans,
  },
  bookingStatusLine: {
    fontSize: 12,
    color: agenda.ink2,
    lineHeight: 16,
    fontFamily: agendaFonts.sans,
  },
  bookingStatusDirty: {
    color: ticketColors.warn,
    fontWeight: '700',
  },
  policyBlock: {
    marginTop: 8,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: agenda.border,
    gap: 4,
  },
  bookingGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 2,
  },
  bookingField: {
    flexGrow: 1,
    flexBasis: '30%',
    minWidth: 120,
  },
  bookingSaveRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 8,
  },
  bookingSaveBtn: {
    marginTop: 0,
    minWidth: 120,
    paddingHorizontal: 18,
  },
  discardBtn: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  discardBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  rowLabel: { fontSize: 14, color: agenda.ink, fontWeight: '600', fontFamily: agendaFonts.sans },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: agenda.ink2,
    marginBottom: 6,
    fontFamily: agendaFonts.sans,
  },
  fieldHelper: {
    fontSize: 11,
    color: agenda.ink2,
    marginTop: 3,
    fontFamily: agendaFonts.sans,
  },
  modeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 4 },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    minHeight: 36,
    justifyContent: 'center',
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    borderWidth: 1,
    borderColor: agenda.border,
  },
  chipActive: { backgroundColor: agenda.brand, borderColor: agenda.brand },
  chipText: { fontSize: 12, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  chipTextActive: { color: agenda.brandInk },
  input: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: agenda.ink,
    backgroundColor: agenda.surface2,
    marginBottom: 6,
    fontSize: 15,
    fontFamily: agendaFonts.sans,
  },
  hint: { fontSize: 12, color: agenda.ink2, marginBottom: 8, lineHeight: 17, fontFamily: agendaFonts.sans },
  listContent: { flexGrow: 1, paddingBottom: 8 },
  primaryBtn: {
    backgroundColor: agenda.brand,
    borderRadius: agenda.radius,
    paddingVertical: 12,
    paddingHorizontal: 14,
    alignItems: 'center',
    marginTop: 4,
  },
  primaryBtnText: { fontWeight: '700', color: agenda.brandInk, fontFamily: agendaFonts.sans },
  btnDisabled: { opacity: 0.6 },
});
